'use client';

import { ReactNode, useEffect, useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { i18n, initI18next, type I18nResources } from '@/lib/i18n';
import { RapidsLocale } from '@/lib/locale';
import { useLabels } from '@/hooks/use-api-endpoint';

interface I18nProviderProps {
  resources: I18nResources;
  locale: RapidsLocale;
  children: ReactNode;
  /**
   * Enable periodic label refreshing from API
   * Allows labels (like WARNING_MESSAGE_BANNER) to update without page refresh
   * @default true
   */
  enableRefresh?: boolean;
  /**
   * Refresh interval in milliseconds
   * @default 5 minutes (300000ms)
   */
  refreshIntervalMs?: number;
}

/**
 * Client-side i18next provider
 * Pre-initialized with server-fetched Contentful resources
 *
 * CRITICAL: Uses useMemo for synchronous initialization to prevent
 * flash of missing content during hydration
 *
 * Features:
 * - Server-side initial rendering (no flash)
 * - Optional client-side periodic refresh for dynamic updates
 */
export function I18nProvider({
  resources,
  locale,
  children,
  enableRefresh = true,
  refreshIntervalMs = 5 * 60 * 1000, // 5 minutes
}: I18nProviderProps) {
  // Initialize synchronously on first render (prevents hydration flash)
  useMemo(() => {
    initI18next(resources, locale);
  }, []);

  // Handle locale changes after initial render
  useEffect(() => {
    if (i18n.isInitialized && i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale]);

  // Enable periodic label refresh if configured
  // This allows labels to update without page refresh (e.g., WARNING_MESSAGE_BANNER)
  const {labels: freshLabels, locale: freshLocale} = useLabels(enableRefresh ? refreshIntervalMs : 0);

  // Update i18next resources when fresh labels arrive
  useEffect(() => {
    if (freshLabels && freshLocale && i18n.isInitialized) {
      i18n.addResources(freshLocale, 'translation', freshLabels);
    }
  }, [freshLabels, freshLocale]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
